import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, forkJoin, Subject, Subscription, switchMap } from 'rxjs';
import { SeasonService } from 'src/app/services/season.service';
import { SeasonPassService } from 'src/app/services/season-pass.service';
import { SeasonPassCategoryService } from 'src/app/services/season-pass-category.service';
import { UserService } from 'src/app/services/user.service';
import { PlacesService } from 'src/app/services/places.service';
import { ProductService } from 'src/app/services/product.service';
import { BlockedSeatsService } from 'src/app/services/blocked-seats.service';
import { PanelTitleService } from 'src/app/services/panel-title.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { InfoCardComponent } from '../../../shared/info-card/info-card.component';
import { SeatsPanelComponent } from '../../../bloques/seats-panel/seats-panel.component';
import { seasonPassSeatLabel } from 'src/app/helpers/season-pass-seat';

@Component({
  selector: 'app-season-pass-form',
  templateUrl: './season-pass-form.component.html',
  styleUrls: ['./season-pass-form.component.scss']
})
export class SeasonPassFormComponent implements OnInit, OnDestroy {

  form: FormGroup;
  isEdit: boolean = false;
  seasonPassId: number | null = null;
  seasons: any[] = [];
  categories: any[] = [];
  spaces: any[] = [];
  editSeason: any = null;
  loaded: boolean = false;
  saving: boolean = false;

  business: any;

  // Alta: buscar usuario existente vs crear uno nuevo
  personMode: 'existing' | 'new' = 'existing';
  userSearchTerm: any = '';
  userSearchResults: any[] = [];
  searchingUsers: boolean = false;
  selectedUser: any = null;
  // Usuario existente sin NIF: se le pide obligatoriamente al seleccionarlo.
  existingUserNeedsNif: boolean = false;
  // Igual con la fecha de nacimiento: si el usuario ya registrado no la tiene,
  // se pide aquí. Es lo que permitirá actualizarle la categoría por edad en
  // renovaciones de temporadas siguientes, así que no se deja pasar ningún
  // alta sin ella.
  existingUserNeedsBirthday: boolean = false;
  // Tope del selector: ayer. La validación real la hace el backend.
  readonly maxBirthDate: string = new Date(Date.now() - 86400000).toISOString().substring(0, 10);

  // Edición: datos del usuario, solo lectura
  readonlyUser: any = null;
  // Edición: el abono completo, para la cabecera de detalle
  pass: any = null;

  // Butaca elegida en el mapa visual (resumen legible para mostrar). null =
  // sin butaca seleccionada por mapa (se conserva la manual/actual si la hay).
  seatSelection: { id_seat: number; row: any; seat: any; sectionName?: string; levelName?: string } | null = null;
  seatMapLoading: boolean = false;

  // Butacas vendidas en algún evento futuro de la compañía (endpoint de
  // ocupadas). No se pintan como ocupadas: son elegibles en el mapa, pero al
  // confirmarlas se avisa de que están vendidas y no se asignan. El backend
  // vuelve a comprobarlo al guardar, así que esto es solo el aviso inmediato.
  private soldSeatIds: number[] = [];

  // El aviso no dice en qué evento está vendida a propósito: el backend solo
  // conoce el conflicto más próximo y, si la butaca está vendida en varios,
  // nombrar uno haría creer que liberándolo se desbloquea.
  private readonly soldSeatMsg = 'Esa butaca no está disponible: ya se ha vendido una entrada para ella en un próximo evento. Elige otra en el mapa.';

  // Cambio de nivel: el producto que paga un abonado depende de su categoría Y
  // del nivel de su butaca, así que al mover la butaca a otro nivel hay que
  // decir a qué tipo de abono pasa (con carnet físico o sin él). Nunca se cobra
  // ni se devuelve la diferencia: eso lo gestiona el club a mano.
  levelChangeProducts: any[] = [];
  levelChangeLoading: boolean = false;
  newLevelId: number | null = null;
  newLevelName: string | null = null;
  selectedNewProductId: number | null = null;

  private userSearchTerms = new Subject<string>();
  private sub: Subscription;
  private searchSub: Subscription;
  private spaceSub?: Subscription;
  private categorySub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private seasonService: SeasonService,
    private seasonPassService: SeasonPassService,
    private categoryService: SeasonPassCategoryService,
    private userService: UserService,
    private placesService: PlacesService,
    private productService: ProductService,
    private panelTitleService: PanelTitleService,
    private blockedSeatsService: BlockedSeatsService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.business = JSON.parse(localStorage.getItem('business') || '{}');

    this.form = this.fb.group({
      id_season: [null],
      id_user: [null],
      name: [''],
      nif: [''],
      email: [''],
      phone: [''],
      birthday: [''],
      id_category: [null],
      id_space: [null],
      id_seat: [null],
      registered_at: [''],
      notes: [''],
    });

    this.searchSub = this.userSearchTerms.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap(term => {
        // La búsqueda es por correo completo y exacto (el backend no admite
        // ningún otro criterio, ver UserService.searchUsersByCompany), así que
        // no se lanza hasta que lo escrito parece un email entero: con un
        // prefijo nunca devolvería nada y solo generaría 422 por cada tecla.
        if (!this.looksLikeEmail(term)) {
          this.userSearchResults = [];
          this.searchingUsers = false;
          return [];
        }
        this.searchingUsers = true;
        return this.userService.searchUsersByCompany(this.business.id, term);
      })
    ).subscribe({
      next: (res: any) => {
        this.userSearchResults = res?.data || [];
        this.searchingUsers = false;
      },
      error: () => {
        this.userSearchResults = [];
        this.searchingUsers = false;
      }
    });

    this.sub = this.route.params.subscribe(params => {
      if (params['passId']) {
        this.isEdit = true;
        this.seasonPassId = +params['passId'];
        this.applyEditValidators();
        // También en edición: la categoría se puede cambiar mientras el abono
        // no tenga ningún cobro pagado (ver canEditCategory).
        this.loadCategories();
        // Los tipos de abono del nivel nuevo dependen de la categoría, así que
        // si esta cambia con un cambio de butaca ya en curso hay que volver a
        // pedirlos: si no, se estarían enseñando los precios de la categoría
        // anterior.
        this.categorySub = this.form.get('id_category')?.valueChanges
          .pipe(distinctUntilChanged())
          .subscribe(() => {
            if (this.levelChanged) this.handleLevelChange(this.newLevelId, this.newLevelName);
          });
        this.loadSeasonPass(this.seasonPassId);
      } else {
        this.loadSeasons();
        this.loadCategories();
        this.loadSpaces();
        this.form.get('id_season')?.setValidators([Validators.required]);
        this.form.get('id_season')?.updateValueAndValidity();
        // La categoría fija qué producto (precio) puede cobrarse luego en el
        // checkout, así que es obligatoria en el alta.
        this.form.get('id_category')?.setValidators([Validators.required]);
        this.form.get('id_category')?.updateValueAndValidity();
        // Y la butaca también: el producto depende además de su nivel, así que
        // un abono sin butaca no podría pagar nada (el backend lo rechaza).
        this.form.get('id_space')?.setValidators([Validators.required]);
        this.form.get('id_space')?.updateValueAndValidity();
        this.form.setValidators(this.seatRequiredValidator);
        this.form.updateValueAndValidity();
        // La butaca solo tiene sentido dentro de su espacio: si se cambia el
        // espacio, hay que volver a elegirla en el mapa del nuevo.
        //
        // `distinctUntilChanged` es imprescindible: al elegir butaca en el mapa
        // se hace `patchValue({ id_seat, id_space })`, y eso reemite `id_space`
        // con el MISMO valor. Sin el filtro, esa reemisión borraba la butaca
        // recién elegida y el formulario seguía pidiéndola.
        this.spaceSub = this.form.get('id_space')?.valueChanges
          .pipe(distinctUntilChanged())
          .subscribe(() => {
            if (this.seatSelection) this.clearSeat();
          });
        this.setPersonMode('existing');
        this.loaded = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.panelTitleService.setSubtitle(null);
    if (this.sub) this.sub.unsubscribe();
    if (this.searchSub) this.searchSub.unsubscribe();
    if (this.spaceSub) this.spaceSub.unsubscribe();
    if (this.categorySub) this.categorySub.unsubscribe();
  }

  loadSeasons() {
    this.seasonService.getSeasonsByCompany(this.business.id).subscribe({
      next: (res) => { this.seasons = res.data || []; }
    });
  }

  loadCategories() {
    this.categoryService.getCategories(true).subscribe({
      next: (res) => { this.categories = res.data || []; }
    });
  }

  loadSpaces() {
    // `all=true` trae todos los espacios de la compañía sin paginar, que es lo
    // que necesita el selector de butaca.
    this.placesService.getListFilterPlaces(this.business.id, 1, null, null, true).subscribe({
      next: (res) => { this.spaces = res?.data?.data || res?.data || []; },
      error: () => { this.spaces = []; }
    });
  }

  private applyEditValidators() {
    ['id_season', 'id_user', 'name', 'nif', 'email', 'birthday'].forEach(f => {
      this.form.get(f)?.clearValidators();
      this.form.get(f)?.updateValueAndValidity();
    });
    this.form.get('registered_at')?.setValidators([Validators.required]);
    this.form.get('registered_at')?.updateValueAndValidity();
  }

  setPersonMode(mode: 'existing' | 'new') {
    this.personMode = mode;
    this.selectedUser = null;
    this.existingUserNeedsNif = false;
    this.existingUserNeedsBirthday = false;
    this.form.patchValue({ id_user: null, name: '', nif: '', email: '', phone: '', birthday: '' });
    this.userSearchTerm = '';
    this.userSearchResults = [];

    if (mode === 'existing') {
      this.form.get('id_user')?.setValidators([Validators.required]);
      this.form.get('name')?.clearValidators();
      this.form.get('nif')?.clearValidators();
      this.form.get('email')?.clearValidators();
      // En Modo A depende del usuario elegido (ver onUserSelected).
      this.form.get('birthday')?.clearValidators();
    } else {
      this.form.get('id_user')?.clearValidators();
      this.form.get('name')?.setValidators([Validators.required]);
      this.form.get('nif')?.setValidators([Validators.required]);
      this.form.get('email')?.setValidators([Validators.required, Validators.email]);
      this.form.get('birthday')?.setValidators([Validators.required]);
    }
    ['id_user', 'name', 'nif', 'email', 'birthday'].forEach(f => this.form.get(f)?.updateValueAndValidity());
  }

  onUserSearchInput() {
    this.selectedUser = null;
    this.clearExistingNifRequirement();
    this.clearExistingBirthdayRequirement();
    this.form.patchValue({ id_user: null });
    // Mientras se escribe, userSearchTerm sigue siendo el texto (string);
    // solo se convierte en objeto cuando se elige una opción del autocomplete.
    if (typeof this.userSearchTerm === 'string') {
      this.userSearchTerms.next(this.userSearchTerm);
    }
  }

  onUserSelected(event: MatAutocompleteSelectedEvent) {
    // Leer el usuario del propio evento y no de `userSearchTerm`: el modelo
    // del input no siempre se ha propagado todavía cuando salta este handler,
    // y si se lee ahí llega como string y `id_user` se queda a null (el botón
    // «Crear abono» no se habilitaba nunca por esto).
    const user = event.option.value;
    this.selectedUser = user;
    this.userSearchTerm = user;
    this.userSearchResults = [];
    this.form.patchValue({ id_user: user?.id ?? null });

    // Si el usuario ya está en la plataforma pero no tiene NIF, se le pide
    // aquí obligatoriamente: el backend lo rellenará en su cuenta al crear el
    // abono. Si ya tiene NIF, ni se muestra ni se toca.
    if (user && !user.nif) {
      this.existingUserNeedsNif = true;
      this.form.get('nif')?.setValidators([Validators.required]);
    } else {
      this.clearExistingNifRequirement();
    }
    this.form.get('nif')?.updateValueAndValidity();

    // Mismo criterio con la fecha de nacimiento: si el usuario ya la tiene en
    // su ficha, ni se muestra ni se toca (el backend tampoco la pisaría); si
    // no, se pide aquí y el alta la rellena en su cuenta.
    if (user && !user.birthday) {
      this.existingUserNeedsBirthday = true;
      this.form.get('birthday')?.setValidators([Validators.required]);
    } else {
      this.clearExistingBirthdayRequirement();
    }
    this.form.get('birthday')?.updateValueAndValidity();
  }

  /** Quita la exigencia de NIF de usuario existente y limpia el campo. */
  private clearExistingNifRequirement() {
    if (!this.existingUserNeedsNif) return;
    this.existingUserNeedsNif = false;
    this.form.get('nif')?.clearValidators();
    this.form.patchValue({ nif: '' });
    this.form.get('nif')?.updateValueAndValidity();
  }

  /** Ídem con la fecha de nacimiento. */
  private clearExistingBirthdayRequirement() {
    if (!this.existingUserNeedsBirthday) return;
    this.existingUserNeedsBirthday = false;
    this.form.get('birthday')?.clearValidators();
    this.form.patchValue({ birthday: '' });
    this.form.get('birthday')?.updateValueAndValidity();
  }

  /** Un email completo, que es lo único que acepta la búsqueda de usuarios. */
  private looksLikeEmail(term: string): boolean {
    return !!term && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(term.trim());
  }

  displayUserFn(user: any): string {
    if (!user) return '';
    if (typeof user === 'string') return user;
    return user.nif ? `${user.name} — ${user.nif}` : user.name;
  }

  loadSeasonPass(id: number) {
    this.seasonPassService.getSeasonPassById(this.business.id, id).subscribe({
      next: (res) => {
        const p = res.data;
        this.pass = p;
        this.readonlyUser = p.user;
        this.editSeason = p.season;
        this.panelTitleService.setSubtitle(p.user?.name || null);
        this.form.patchValue({
          registered_at: p.registered_at ? p.registered_at.substring(0, 10) : '',
          notes: p.notes || '',
          id_category: p.id_category ? +p.id_category : null,
          // El espacio del abono es la ancla para abrir el mapa y reasignar butaca.
          id_space: p.id_space || null,
        });
        // Resumen de la butaca actual (si tiene) para el bloque de butaca. El
        // nivel llega con el abono, así que el resumen ya lo enseña sin tener
        // que abrir el mapa; la sección no, que solo se conoce al elegir en el
        // mapa (el abono guarda su PK, no su nombre).
        if (p.seat != null && p.seat !== '') {
          this.seatSelection = {
            id_seat: 0, // desconocido hasta reabrir el mapa; solo para mostrar
            row: p.row,
            seat: p.seat,
            levelName: p.level?.name,
          };
        }
        this.loaded = true;
      },
      error: () => {
        this.openInfoCard('Error al cargar el abono');
        this.router.navigate(['/panel-control/sports-passes/passes']);
      }
    });
  }

  /**
   * Abre el mapa visual de butacas del espacio seleccionado para elegir/cambiar
   * la butaca. Marca como ocupadas las de abonos de plataforma + socios legacy
   * (endpoint season-pass-occupied-seats) y, en edición, excluye/preselecciona
   * la butaca propia del abono. Solo el Palacio de los Deportes tiene mapa; sin
   * mapa no se puede dar de alta un abono en ese espacio.
   *
   * Los bloqueos del recinto se muestran igual que en el flujo público, pero
   * aquí el operador puede liberarlos con el interruptor del mapa para dar de
   * alta un abono encima (ver SeatsPanelComponent.toggleBlockedSelection), así
   * que además de las butacas se piden los grupos con su nombre y color.
   */
  openSeatMap() {
    const spaceId = this.form.value.id_space;
    if (!spaceId || this.seatMapLoading || !this.canEditSeat) return;
    this.seatMapLoading = true;
    const excludePassId = this.isEdit && this.seasonPassId ? this.seasonPassId : undefined;

    forkJoin({
      place: this.placesService.getPlaceById(spaceId),
      occupied: this.seasonPassService.getOccupiedSeats(this.business.id, spaceId, excludePassId),
      blockedGroups: this.blockedSeatsService.getSpaceBlockedGroups(spaceId),
    }).subscribe({
      next: ({ place, occupied, blockedGroups }: any) => {
        this.seatMapLoading = false;
        const map = place?.data?.map;
        if (!map) {
          this.openInfoCard('Este espacio no tiene un mapa de butacas configurado, así que no se le pueden asignar butacas.');
          return;
        }
        const occ = occupied?.data || {};
        this.soldSeatIds = (occ.sold_seat_ids || []).map((id: any) => +id);
        this.openSeatDialog(spaceId, map, occ.occupied_seat_ids || [], occ.blocked_seat_ids || [], blockedGroups || [], occ.own_seat_id ?? null);
      },
      error: () => {
        this.seatMapLoading = false;
        this.openInfoCard('No se ha podido cargar el mapa de butacas.');
      }
    });
  }

  private openSeatDialog(spaceId: number, map: any, occupiedSeatIds: number[], blockedSeatIds: number[], blockedGroups: any[], currentSeatId: number | null) {
    const dialogRef = this.dialog.open(SeatsPanelComponent, {
      panelClass: ['info-dialog', 'not-padding', 'panel-seats'],
      data: {
        seasonPassMode: true,
        // La ocupación va en booked_seats para reutilizar el mecanismo del mapa
        // de compra sin tocar el render (ver SeatsPanelComponent.ngOnInit).
        //
        // `blocked_seats` son los bloqueos permanentes del recinto (PMR,
        // cabinas, butacas retiradas): no viajan dentro del mapa, llegan aparte
        // del endpoint de ocupadas, y sin ellos el panel las dejaría asignar
        // como butaca de abono.
        mapEvent: { ...map, booked_seats: occupiedSeatIds, reserved_seats: [], blocked_seats: blockedSeatIds },
        // Solo el panel puede liberar esos bloqueos: en el flujo público (alta y
        // vinculación de carné) son intocables.
        allowBlockedSelection: true,
        blockedGroups,
        currentSeatId,
      }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result || !result.id_seat) return;

      // Vendida en un evento: el abono no puede quedarse con ella (el abonado
      // se quedaría sin sitio en ese evento), así que se descarta la selección
      // y se explica por qué. Mismo criterio que aplica el backend al guardar.
      if (this.soldSeatIds.includes(+result.id_seat)) {
        this.openInfoCard(this.soldSeatMsg);
        return;
      }

      this.seatSelection = {
        id_seat: result.id_seat,
        row: result.row,
        seat: result.seat,
        sectionName: result.section?.name,
        levelName: result.level?.name,
      };
      // El backend deriva id_level/id_section/row/seat de id_seat, así que en el
      // formulario basta con la butaca y su espacio.
      this.form.patchValue({
        id_seat: result.id_seat,
        id_space: spaceId,
      });

      this.handleLevelChange(result.level?.id ?? null, result.level?.name ?? null);
    });
  }

  /**
   * La butaca nueva es de otro nivel: se cargan los tipos de abono de ese nivel
   * para la categoría del abonado y el admin elige a cuál pasa (con carnet o
   * sin él). Si es el mismo nivel, no hay nada que decidir.
   *
   * Se incluyen los productos de canal panel: mover de nivel a un abonado que
   * está en una invitación no debe obligar a sacarlo de ella.
   */
  private handleLevelChange(levelId: number | null, levelName: string | null) {
    this.resetLevelChange();

    if (!this.isEdit || !levelId) return;

    const currentLevelId = this.pass?.id_level != null && this.pass.id_level !== '' ? +this.pass.id_level : null;
    if (+levelId === currentLevelId) return;

    const seasonId = this.pass?.season?.id;
    // La categoría del formulario, no la guardada: si el operador la está
    // cambiando en esta misma edición (solo posible sin cobro pagado), los
    // abonos del nivel nuevo son los de la categoría nueva.
    const categoryId = this.form.value.id_category ?? this.pass?.id_category;
    if (!seasonId) return;

    this.newLevelId = +levelId;
    this.newLevelName = levelName;
    this.levelChangeLoading = true;

    this.productService.getProductsBySeason(seasonId, categoryId, +levelId, true).subscribe({
      next: (res) => {
        this.levelChangeProducts = (res.data || []).filter((p: any) => p.active !== false);
        // Con una sola opción no hay elección real que hacer.
        if (this.levelChangeProducts.length === 1) {
          this.selectedNewProductId = +this.levelChangeProducts[0].id;
        }
        this.levelChangeLoading = false;
      },
      error: () => {
        this.levelChangeProducts = [];
        this.levelChangeLoading = false;
      }
    });
  }

  private resetLevelChange() {
    this.levelChangeProducts = [];
    this.levelChangeLoading = false;
    this.newLevelId = null;
    this.newLevelName = null;
    this.selectedNewProductId = null;
  }

  selectNewProduct(productId: number) {
    this.selectedNewProductId = +productId;
  }

  /** Tipo de abono contratado hoy, según su última renovación. */
  get currentProduct(): any {
    return this.pass?.latest_renewal?.product || null;
  }

  /** Solo hay que reasignar producto si el abono ya tiene un pago cobrado. */
  get hasPaidRenewal(): boolean {
    return this.pass?.latest_renewal?.status?.short_name === 'paid';
  }

  /**
   * La categoría se puede cambiar en edición mientras el abono NO tenga un
   * cobro pagado: hasta entonces no hay importe comprometido y el producto
   * (precio) se resuelve al cobrar, ya con la categoría nueva. Un intento de
   * pago abandonado —renovación pendiente— no cuenta: no ha movido dinero.
   *
   * En cuanto hay un cobro pagado se bloquea: cambiar la categoría dejaría esa
   * renovación apuntando al producto de otra categoría, y no hay reajuste que
   * lo arregle (a diferencia del cambio de nivel, que sí reasigna producto).
   */
  get canEditCategory(): boolean {
    return this.isEdit && !this.hasPaidRenewal;
  }

  /**
   * La butaca solo se puede reasignar mientras el abono esté «Pendiente»: es el
   * único estado en el que no puede tener reservas generadas. Las de los
   * eventos publicados se crean solo para abonos activos
   * (SeasonPassEventBookingService) y nadie las mueve al editar el abono, así
   * que cambiar la butaca después dejaría al abonado con una butaca en su carné
   * y otra distinta al escanear en la puerta. Para moverlo de sitio hay que
   * anular el abono y darlo de alta en la butaca nueva.
   *
   * `inactive` NO entra aunque siga admitiendo cobro (ver canCharge): un abono
   * desactivado puede haber estado activo antes y tener ya sus reservas hechas.
   *
   * PENDIENTE: replicarlo en el backend (`SeasonPassController::update` sigue
   * aceptando `id_seat` en cualquier estado), que es quien de verdad lo tiene
   * que impedir.
   */
  get canEditSeat(): boolean {
    return !this.isEdit || this.pass?.status?.slug === 'pending';
  }

  get levelChanged(): boolean {
    return this.newLevelId !== null;
  }

  get needsProductChoice(): boolean {
    return this.levelChanged && this.hasPaidRenewal;
  }

  /** Diferencia informativa entre lo contratado y el tipo elegido: no se cobra. */
  get priceDifference(): number | null {
    const current = this.currentProduct?.price != null ? +this.currentProduct.price : null;
    const chosen = this.levelChangeProducts.find(p => +p.id === this.selectedNewProductId);
    if (current === null || !chosen) return null;
    return +chosen.price - current;
  }

  /**
   * Descarta la butaca elegida. No se ofrece al operador (un abono siempre debe
   * tener butaca): solo se usa al cambiar de espacio, porque la butaca ya no
   * pertenece al mapa nuevo.
   */
  private clearSeat() {
    this.seatSelection = null;
    this.form.patchValue({ id_seat: null });
    this.resetLevelChange();
  }

  /**
   * La butaca es obligatoria en el alta y solo puede venir del mapa (`id_seat`):
   * no se admite introducirla a mano.
   */
  private seatRequiredValidator(control: AbstractControl): ValidationErrors | null {
    const v = control.value || {};
    return v.id_seat ? null : { seatRequired: true };
  }

  save() {
    if (this.form.invalid || this.saving) return;
    // El abono ya está cobrado y la butaca nueva es de otro nivel: no se guarda
    // hasta saber a qué tipo de abono pasa (el backend lo rechazaría igual).
    if (this.needsProductChoice && !this.selectedNewProductId) return;
    this.saving = true;

    if (this.isEdit && this.seasonPassId) {
      const payload: any = {
        registered_at: this.form.value.registered_at,
        notes: this.form.value.notes || null,
      };
      // Categoría: solo si el abono aún no tiene cobro pagado (canEditCategory).
      // Si no, ni se muestra el selector ni se manda, para no tocarla nunca.
      if (this.canEditCategory && this.form.value.id_category) {
        payload.id_category = this.form.value.id_category;
      }
      // Reasignación de butaca desde el mapa (opcional): el backend deriva el
      // resto de campos de id_seat. Sin id_seat, la butaca no se toca — que es
      // el caso de un abono ya cobrado (canEditSeat), donde ni se manda.
      if (this.canEditSeat && this.form.value.id_seat) {
        payload.id_space = this.form.value.id_space;
        payload.id_seat = this.form.value.id_seat;
      }
      // Butaca de otro nivel en un abono ya cobrado: el tipo de abono pasa a
      // ser el del nivel nuevo. Sin cobros ni devoluciones.
      if (this.needsProductChoice && this.selectedNewProductId) {
        payload.id_product = this.selectedNewProductId;
      }
      this.seasonPassService.updateSeasonPass(this.business.id, this.seasonPassId, payload).subscribe({
        next: () => {
          this.saving = false;
          this.openInfoCard('Abono actualizado correctamente');
          this.router.navigate(['/panel-control/sports-passes/passes']);
        },
        error: (err) => {
          this.saving = false;
          this.openInfoCard(this.extractErrorMessage(err, 'Error al actualizar el abono'));
        }
      });
      return;
    }

    const v = this.form.value;
    const payload: any = { id_season: v.id_season, id_category: v.id_category };

    if (this.personMode === 'existing') {
      payload.id_user = v.id_user;
      // Solo cuando el usuario no tenía NIF y se le ha pedido en el formulario.
      if (this.existingUserNeedsNif && v.nif) payload.nif = v.nif;
      // Ídem con la fecha de nacimiento.
      if (this.existingUserNeedsBirthday && v.birthday) payload.birthday = v.birthday;
    } else {
      payload.name = v.name;
      payload.nif = v.nif;
      payload.email = v.email;
      payload.birthday = v.birthday;
      if (v.phone) payload.phone = v.phone;
    }

    // El nº de socio no se manda nunca: lo calcula el backend a partir del
    // espacio (SeasonPass::nextMemberNumberForSpace), continuando la
    // numeración histórica del carné migrado más alto de ese espacio.

    // Butaca: siempre elegida en el mapa, así que basta con el espacio y el
    // id_seat — el backend deriva nivel, sección, fila y asiento.
    if (v.id_space) {
      payload.id_space = v.id_space;
      if (v.id_seat) payload.id_seat = v.id_seat;
    }

    if (v.registered_at) payload.registered_at = v.registered_at;
    if (v.notes) payload.notes = v.notes;

    this.seasonPassService.createSeasonPass(this.business.id, payload).subscribe({
      next: (res) => {
        this.saving = false;
        const created = res?.data;
        // Recién creado el abono, el siguiente paso es cobrarlo: se lleva al
        // checkout del panel. Si por lo que sea no llega el id, se cae al
        // listado para no dejar al usuario en blanco.
        const goNext = () => {
          if (created?.id) {
            this.router.navigate(['/panel-control/sports-passes/passes/checkout', created.id]);
          } else {
            this.router.navigate(['/panel-control/sports-passes/passes']);
          }
        };

        // Usuario nuevo: se le ha creado una cuenta de Citylok. Avisamos (para
        // que el operador se lo traslade) de que el usuario recibirá un email y
        // debe establecer su contraseña para poder entrar. Se continúa al
        // siguiente paso tras cerrar el aviso.
        if (created?.account_created) {
          this.openInfoCard(
            '<strong class="info-card-title">Se ha creado una cuenta de Citylok</strong>'
            + '<span class="info-card-lead">Hemos enviado un correo al usuario con el email facilitado.</span>'
            + '<span class="info-card-lead">Para acceder y ver su abono deberá establecer una contraseña siguiendo estos pasos:</span>'
            + '<ul>'
            + '<li>Entrar en el inicio de sesión de Citylok.</li>'
            + '<li>Pulsar en <strong>«¿Has olvidado la contraseña?»</strong>.</li>'
            + '<li>Usar ese mismo email para crear la contraseña.</li>'
            + '</ul>'
          ).afterClosed().subscribe(() => goNext());
        } else {
          goNext();
        }
      },
      error: (err) => {
        this.saving = false;
        this.openInfoCard(this.extractErrorMessage(err, 'Error al crear el abono'));
      }
    });
  }

  cancel() {
    this.router.navigate(['/panel-control/sports-passes/passes']);
  }

  /** Iniciales del abonado para el avatar de la cabecera. */
  get userInitials(): string {
    const name: string = this.readonlyUser?.name || '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
  }

  /** Mismos estados y colores que la tabla de abonados, para no despistar. */
  get statusBadgeClass(): string {
    const slug = this.pass?.status?.slug;
    if (slug === 'active') return 'status-badge--active';
    if (slug === 'pending') return 'status-badge--pending';
    if (slug === 'cancelled' || slug === 'expired') return 'status-badge--ended';
    return 'status-badge--inactive';
  }

  get statusIcon(): string {
    const slug = this.pass?.status?.slug;
    if (slug === 'active') return 'fa-circle-check';
    if (slug === 'pending') return 'fa-circle-pause';
    if (slug === 'cancelled' || slug === 'expired') return 'fa-circle-xmark';
    return 'fa-circle-minus';
  }

  get seatDisplay(): string | null {
    return seasonPassSeatLabel(this.pass);
  }

  /**
   * Abono todavía por cobrar (típicamente un alta hecha con «Cobrar más
   * tarde»): no está activo ni ha terminado. Con butaca ya asignada se puede
   * llevar a la taquilla; sin ella no hay nivel y por tanto ningún importe.
   */
  get canCharge(): boolean {
    const slug = this.pass?.status?.slug;
    return this.isEdit && (slug === 'pending' || slug === 'inactive');
  }

  get canChargeNow(): boolean {
    return this.canCharge && !!this.pass?.seat;
  }

  goToCheckout() {
    if (!this.canChargeNow) return;
    this.router.navigate(['/panel-control/sports-passes/passes/checkout', this.seasonPassId]);
  }

  /**
   * Qué le falta al formulario para poder guardarse. Se muestra junto al botón
   * para que no quede «apagado» sin explicación.
   */
  get missingFields(): string[] {
    if (this.isEdit) {
      const missingEdit: string[] = [];
      if (this.form.get('registered_at')?.invalid) missingEdit.push('la fecha de alta');
      if (this.needsProductChoice && !this.selectedNewProductId) missingEdit.push('el tipo de abono del nivel nuevo');
      return missingEdit;
    }
    const missing: string[] = [];
    if (this.form.get('id_season')?.invalid) missing.push('la temporada');
    if (this.form.get('id_category')?.invalid) missing.push('la categoría');
    if (this.form.get('id_space')?.invalid) missing.push('el recinto');
    if (this.form.errors?.['seatRequired']) missing.push('la butaca');
    if (this.personMode === 'existing') {
      if (this.form.get('id_user')?.invalid) missing.push('el usuario abonado');
      if (this.existingUserNeedsNif && this.form.get('nif')?.invalid) missing.push('el NIF del usuario');
      if (this.existingUserNeedsBirthday && this.form.get('birthday')?.invalid) missing.push('la fecha de nacimiento del usuario');
    } else {
      if (this.form.get('name')?.invalid) missing.push('el nombre');
      if (this.form.get('nif')?.invalid) missing.push('el NIF');
      if (this.form.get('email')?.invalid) missing.push('un email válido');
      if (this.form.get('birthday')?.invalid) missing.push('la fecha de nacimiento');
    }
    return missing;
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 422 && err?.error?.errors && typeof err.error.errors === 'object' && !Array.isArray(err.error.errors)) {
      const first: any = Object.values(err.error.errors)[0];
      if (Array.isArray(first) && first.length) {
        return first[0];
      }
    }
    return err?.error?.message || fallback;
  }

  private openInfoCard(msg: string) {
    return this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text: msg }
    });
  }
}
